import { Button, Flex, Text, Card, Badge } from "@radix-ui/themes";
import { PlusIcon } from "@radix-ui/react-icons";
import { useCallback, useMemo, useState } from "react";
import { JsonSchema7BuilderProps, JsonSchema7Property } from "./types";
import { PropertyEditor } from "./PropertyEditor";
import { SchemaPreview } from "./SchemaPreview";
import { generateId } from "./utils";
import { JSONSchema7 } from "json-schema";

export function JsonSchema7Builder({
  schema,
  onChange,
  title = "JSON Schema Builder",
  className,
}: JsonSchema7BuilderProps) {
  const [properties, setProperties] = useState<JsonSchema7Property[]>(() => {
    if (
      schema &&
      typeof schema === "object" &&
      schema.type === "object" &&
      schema.properties
    ) {
      const requiredFields = schema.required || [];
      return Object.entries(schema.properties)
        .map(([name, prop]) => {
          // JSONSchema7 properties can be JSONSchema7Definition (which includes boolean)
          // but we only want to process actual JSONSchema7 objects
          if (typeof prop === "boolean") return null;
          return parseSchemaProperty(name, prop, requiredFields.includes(name));
        })
        .filter((prop): prop is JsonSchema7Property => prop !== null);
    }
    return [];
  });

  const handleChange = useCallback(
    (updatedProperties: JsonSchema7Property[]) => {
      setProperties(updatedProperties);
      const newSchema = generateSchema(updatedProperties);
      onChange(newSchema);
    },
    [onChange]
  );

  const addProperty = useCallback(() => {
    const newProperty: JsonSchema7Property = {
      id: generateId(),
      name: `property_${Date.now()}`,
      type: "string",
      required: false,
      validation: {},
    };

    const updatedProperties = [...properties, newProperty];
    handleChange(updatedProperties);
  }, [properties, handleChange]);

  const updateProperty = useCallback(
    (propertyId: string, updatedProperty: JsonSchema7Property) => {
      const updatedProperties = properties.map((prop) =>
        prop.id === propertyId ? updatedProperty : prop
      );
      handleChange(updatedProperties);
    },
    [properties, handleChange]
  );

  const deleteProperty = useCallback(
    (propertyId: string) => {
      const updatedProperties = properties.filter(
        (prop) => prop.id !== propertyId
      );
      handleChange(updatedProperties);
    },
    [properties, handleChange]
  );

  const currentSchema = useMemo(() => {
    return generateSchema(properties);
  }, [properties]);

  return (
    <Flex direction="column" gap="4" className={className}>
      <Flex gap="4" className="flex-1">
        <Flex direction="column" className="flex-1">
          <Flex justify="between" align="center" className="mb-4">
            <Text size="4" weight="bold">
              {title}
            </Text>
            <Button
              size="2"
              variant="solid"
              color="gray"
              highContrast
              onClick={addProperty}
            >
              <PlusIcon />
              Add Property
            </Button>
          </Flex>

          <Flex direction="column" gap="2">
            {properties.length === 0 ? (
              <Text size="2" color="gray">
                No properties defined. Click "Add Property" to get started.
              </Text>
            ) : (
              properties.map((property) => (
                <PropertyEditor
                  key={property.id}
                  property={property}
                  onChange={(updatedProperty) =>
                    updateProperty(property.id, updatedProperty)
                  }
                  onDelete={() => deleteProperty(property.id)}
                />
              ))
            )}
          </Flex>
        </Flex>

        <SchemaPreview schema={currentSchema} />
      </Flex>
    </Flex>
  );
}

// Helper function to parse a JSON Schema property into our internal format
function parseSchemaProperty(
  name: string,
  prop: any,
  isRequired: boolean = false
): JsonSchema7Property {
  const id = generateId();

  return {
    id,
    name,
    type: prop.type || "string",
    description: prop.description,
    title: prop.title,
    required: isRequired,
    properties: prop.properties
      ? Object.entries(prop.properties)
          .map(([n, p]) => {
            // For nested properties, we need to check if they're in the parent's required array
            // Skip boolean properties as they're not valid JSONSchema7 objects
            if (typeof p === "boolean") return null;
            const nestedRequired = prop.required
              ? prop.required.includes(n)
              : false;
            return parseSchemaProperty(n, p, nestedRequired);
          })
          .filter((prop): prop is JsonSchema7Property => prop !== null)
      : undefined,
    items:
      prop.items &&
      typeof prop.items === "object" &&
      typeof prop.items !== "boolean"
        ? parseSchemaProperty("item", prop.items, false)
        : undefined,
    validation: {
      minLength: prop.minLength,
      maxLength: prop.maxLength,
      pattern: prop.pattern,
      minimum: prop.minimum,
      maximum: prop.maximum,
      exclusiveMinimum: prop.exclusiveMinimum,
      exclusiveMaximum: prop.exclusiveMaximum,
      multipleOf: prop.multipleOf,
      minItems: prop.minItems,
      maxItems: prop.maxItems,
      uniqueItems: prop.uniqueItems,
      minProperties: prop.minProperties,
      maxProperties: prop.maxProperties,
      format: prop.format,
    },
    enum: prop.enum,
    const: prop.const,
    default: prop.default,
    examples: prop.examples,
  };
}

// Helper function to generate JSON Schema from our internal format
function generateSchema(properties: JsonSchema7Property[]): JSONSchema7 {
  const schema: JSONSchema7 = {
    $schema: "http://json-schema.org/draft-07/schema#",
    type: "object",
    properties: {},
  };

  properties.forEach((prop) => {
    const schemaProp = generatePropertySchema(prop);
    schema.properties![prop.name] = schemaProp;
  });

  const requiredProps = properties.filter((p) => p.required).map((p) => p.name);
  if (requiredProps.length > 0) {
    schema.required = requiredProps;
  }

  return schema;
}

function generatePropertySchema(property: JsonSchema7Property): JSONSchema7 {
  const schema: JSONSchema7 = {
    type: property.type,
  };

  if (property.title) schema.title = property.title;
  if (property.description) schema.description = property.description;
  if (property.default !== undefined) schema.default = property.default;
  if (property.examples !== undefined) schema.examples = property.examples;
  if (property.enum) schema.enum = property.enum;
  if (property.const !== undefined) schema.const = property.const;

  // Add validation properties
  if (property.validation) {
    const { validation } = property;
    if (validation.minLength !== undefined)
      schema.minLength = validation.minLength;
    if (validation.maxLength !== undefined)
      schema.maxLength = validation.maxLength;
    if (validation.pattern) schema.pattern = validation.pattern;
    if (validation.minimum !== undefined) schema.minimum = validation.minimum;
    if (validation.maximum !== undefined) schema.maximum = validation.maximum;
    if (validation.exclusiveMinimum !== undefined)
      schema.exclusiveMinimum = validation.exclusiveMinimum;
    if (validation.exclusiveMaximum !== undefined)
      schema.exclusiveMaximum = validation.exclusiveMaximum;
    if (validation.multipleOf !== undefined)
      schema.multipleOf = validation.multipleOf;
    if (validation.minItems !== undefined)
      schema.minItems = validation.minItems;
    if (validation.maxItems !== undefined)
      schema.maxItems = validation.maxItems;
    if (validation.uniqueItems !== undefined)
      schema.uniqueItems = validation.uniqueItems;
    if (validation.minProperties !== undefined)
      schema.minProperties = validation.minProperties;
    if (validation.maxProperties !== undefined)
      schema.maxProperties = validation.maxProperties;
    if (validation.format) schema.format = validation.format;
  }

  // Handle nested properties for objects
  if (property.type === "object" && property.properties) {
    schema.properties = {};
    property.properties.forEach((nestedProp) => {
      schema.properties![nestedProp.name] = generatePropertySchema(nestedProp);
    });

    const requiredNested = property.properties
      .filter((p) => p.required)
      .map((p) => p.name);
    if (requiredNested.length > 0) {
      schema.required = requiredNested;
    }
  }

  // Handle array items
  if (property.type === "array" && property.items) {
    schema.items = generatePropertySchema(property.items);
  }

  return schema;
}
